import { useCallback, useEffect, useState } from "react";
import { clientLogin, clientRefreshSession, SESSION_EXPIRED_EVENT } from "../lib/api";
import { goToSessionExpired } from "../pages/StatusPages";

const TOKEN_KEY = "lc_client_token_v1";
const MSG_KEY = "lc_session_msg";
const ENDED = "Your session has ended — please sign in again.";

export function useClientAuth() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(() => {
    const m = sessionStorage.getItem(MSG_KEY) || "";
    sessionStorage.removeItem(MSG_KEY);
    return m;
  });

  const clear = useCallback((msg = "") => {
    localStorage.removeItem(TOKEN_KEY);
    if (msg) sessionStorage.setItem(MSG_KEY, msg);
    setToken("");
    setProfile(null);
    setError(msg);
    if (msg === ENDED) goToSessionExpired();
  }, []);

  // Validates the session and renews the token (sliding expiry)
  const refresh = useCallback(async (t) => {
    const useTok = t || token;
    if (!useTok) {
      setProfile(null);
      setLoading(false);
      return;
    }
    try {
      const r = await clientRefreshSession(useTok);
      localStorage.setItem(TOKEN_KEY, r.token);
      setToken(r.token);
      setProfile(r.client);
    } catch (err) {
      if (err?.response?.status === 401) clear(ENDED);
    } finally {
      setLoading(false);
    }
  }, [token, clear]);

  useEffect(() => {
    refresh();
    const onExpired = (e) => clear(e.detail === "logout" ? "" : ENDED);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (identifier, pin) => {
    setError("");
    try {
      const r = await clientLogin(identifier, pin);
      localStorage.setItem(TOKEN_KEY, r.token);
      setToken(r.token);
      setProfile(r.client);
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid credentials";
      setError(typeof msg === "string" ? msg : "Login failed");
      return false;
    }
  };

  const logout = () => {
    clear();
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: "logout" }));
  };

  return { token, profile, loading, error, login, logout, refresh };
}
